export const themeRoundnessMultiplier = 2;
/** Matches react-native-paper's MD3 Button radius: 5 * theme.roundness. */
export const buttonRoundnessMultiplier = 5;

import type { AppConfigMasteryLevelsType } from "@memorang/constants/config/schema";
import type { MixedItemTag } from "@memorang/types";
import { type IconType, type LevelIconName, isLevelIconName } from "./icon";

export const questionTypeIconMap: Record<string, IconType> = {
	Photograph: "camera-outline",
	Photographs: "camera-outline",
	"Question Response": "chat-question-outline",
	Conversation: "forum-outline",
	Conversations: "forum-outline",
	Talks: "microphone-outline",
	"Incomplete Sentences": "text-box-edit-outline",
	"Text Completion": "format-text",
	"Single Passage": "file-document-outline",
	"Multiple Passage": "file-multiple-outline",
	"Reading Comprehension": "book-open-outline",
	"Reading Comprehension Passages": "book-open-outline",
	"Repeat Sentence": "waveform",
	"Read Aloud": "account-tie-voice-outline",
	"Describe Image": "message-image-outline",
	"Write Essay": "pencil-outline",
	"Retell Lecture": "bullhorn-outline",
	"Summarize Group Discussion": "forum-outline",
	"Answer Short Question": "chat-question-outline",
	"Respond to a Situation": "message-outline",
	"Summarize Written Text": "text-box-outline",
	"Reorder Paragraph": "format-list-numbered",
	"Fill in the Blanks (Dropdown)": "form-dropdown",
	"Fill in the Blanks (Type In)": "form-textbox",
	"Fill in the Blanks (Drag and Drop)": "drag-variant",
	"Fill in the Blanks": "subtitles-outline",
	"Reading and Writing: Fill in the Blanks": "subtitles-outline",
	"Multiple Choice, Single Answer": "list-status",
	"Multiple Choice, Single Answer (Reading)": "list-status",
	"Multiple Choice, Single Answer (Listening)": "list-status",
	"Multiple Choice, Multiple Answers": "format-list-checks",
	"Multiple Choice, Multiple Answers (Reading)": "format-list-checks",
	"Multiple Choice, Multiple Answers (Listening)": "format-list-checks",
	"Summarize Spoken Text": "headphones",
	"Highlight Correct Summary": "text-box-check-outline",
	"Select Missing Word": "text-search",
	"Highlight Incorrect Words": "format-color-highlight",
	"Write from Dictation": "keyboard-outline",
	"Write Email": "email-edit-outline",
	"Personal Introduction Speaking & Writing": "handshake-outline",
};

export const skillIconMap: Record<string, IconType> = {
	Writing: "file-document-edit-outline",
	写作: "file-document-edit-outline",
	ライティング: "file-document-edit-outline",
	Listening: "ear-hearing",
	听力: "ear-hearing",
	リスニング: "ear-hearing",
	Speaking: "microphone-outline",
	口语: "microphone-outline",
	スピーキング: "microphone-outline",
	Reading: "book-open-page-variant-outline",
	阅读: "book-open-page-variant-outline",
	読解: "book-open-page-variant-outline",
	リーディング: "book-open-page-variant-outline",
	"Reading & Listening": "book-open-page-variant-outline",
	"Speaking & Writing": "microphone-outline",
};

export const getSkillIcon = (skillTitle: string): IconType =>
	skillIconMap[skillTitle] ?? "school-outline";

export const getQuestionTypeIcon = (questionTypeTitle: string): IconType =>
	questionTypeIconMap[questionTypeTitle] ?? "text-box-outline";

export const resolveTagIcon = (
	tag: Pick<MixedItemTag, "title" | "icon">,
	fallbackMap: Record<string, LevelIconName>,
): LevelIconName | undefined => {
	const name = typeof tag.icon === "string" ? tag.icon : tag.icon?.name;
	if (name && isLevelIconName(name)) {
		return name;
	}
	return fallbackMap[tag.title];
};

export const MAX_ITEM_TYPE_ROWS = 4;

export const getProficiencyLevelIcons = (
	proficiencyLevels?: AppConfigMasteryLevelsType,
): LevelIconName[] =>
	[...(proficiencyLevels ?? [])]
		.sort((a, b) => a.sortOrder - b.sortOrder)
		.map((level) => level.icon)
		.filter(isLevelIconName);

export const buildProficiencyIconMap = (
	proficiencyLevels?: AppConfigMasteryLevelsType,
): Record<string, LevelIconName> => {
	const iconMap: Record<string, LevelIconName> = {};
	for (const level of proficiencyLevels ?? []) {
		if (isLevelIconName(level.icon)) {
			iconMap[level.alias.title] = level.icon;
		}
	}
	return iconMap;
};
